import { normalizeTaskShape } from "./taskUpdateHelper";
import { getTaskPrioritySortRank } from "../utils/taskPriority";
import {
  TABLE_EMPTY_CELL,
  truncateTableText,
  formatPersonDisplay,
  formatPropertyFullAddress,
  formatCreatedByDisplay,
} from "../utils/tableDisplayUtils";

function isPropertyTaskGroup(item) {
  return item != null && typeof item === "object" && Array.isArray(item.tasks) && item.property_id != null;
}

function enrichTaskFromPropertyGroup(task, group) {
  const address =
    (typeof task.full_address === "string" && task.full_address.trim()) ||
    (typeof group.full_address === "string" && group.full_address.trim()) ||
    null;

  return normalizeTaskShape({
    ...task,
    property_id: task.property_id ?? group.property_id,
    full_address: address ?? task.full_address,
    property_address: address ?? task.property_address,
  });
}

function splitTasksByStatus(list) {
  const active = [];
  const finished = [];
  for (const task of list) {
    if (task?.status === "completed") {
      finished.push(task);
    } else {
      active.push(task);
    }
  }
  return { active, finished };
}

function groupFlatTasksByProperty(tasks) {
  const byProperty = new Map();

  for (const task of tasks) {
    const propertyId = task?.property_id ?? task?.property?.id;
    if (propertyId == null) continue;

    const key = String(propertyId);
    if (!byProperty.has(key)) {
      byProperty.set(key, {
        property_id: propertyId,
        full_address: formatPropertyFullAddress(task),
        tasks: [],
      });
    }
    byProperty.get(key).tasks.push(task);
  }

  return [...byProperty.values()];
}

/**
 * Flattens API property groups into one task list, preserving property order.
 * Tasks for the same property stay consecutive.
 */
function flattenPropertyGroupsByTaskStatus(groups) {
  const active = [];
  const finished = [];

  for (const group of groups) {
    for (const task of group.tasks ?? []) {
      const enriched = enrichTaskFromPropertyGroup(task, group);
      if (enriched.status === "completed") {
        finished.push(enriched);
      } else {
        active.push(enriched);
      }
    }
  }

  return { active, finished };
}

/**
 * Normalizes get-assigned-tasks API payload into flat task lists (active / finished),
 * grouped by property in API order within each list.
 */
export function normalizeAssignedTasksPayload(raw) {
  if (raw == null) return { active: [], finished: [] };

  const body = raw.data !== undefined && raw.data !== null ? raw.data : raw;

  if (Array.isArray(body)) {
    if (body.length > 0 && isPropertyTaskGroup(body[0])) {
      return flattenPropertyGroupsByTaskStatus(body);
    }
    return flattenPropertyGroupsByTaskStatus(groupFlatTasksByProperty(body));
  }

  if (typeof body === "object") {
    if (Array.isArray(body.properties)) {
      return flattenPropertyGroupsByTaskStatus(body.properties);
    }

    const active = body.active_tasks ?? body.active ?? body.not_completed ?? [];
    const finished = body.finished_tasks ?? body.finished ?? body.completed ?? [];

    if (Array.isArray(active) || Array.isArray(finished)) {
      const merged = [...(Array.isArray(active) ? active : []), ...(Array.isArray(finished) ? finished : [])];
      if (merged.length > 0 && isPropertyTaskGroup(merged[0])) {
        return flattenPropertyGroupsByTaskStatus(merged);
      }
      const { active: activeTasks, finished: finishedTasks } = splitTasksByStatus([
        ...(Array.isArray(active) ? active : []),
        ...(Array.isArray(finished) ? finished : []),
      ]);
      return flattenPropertyGroupsByTaskStatus(groupFlatTasksByProperty([...activeTasks, ...finishedTasks]));
    }

    if (Array.isArray(body.tasks)) {
      if (body.tasks.length > 0 && isPropertyTaskGroup(body.tasks[0])) {
        return flattenPropertyGroupsByTaskStatus(body.tasks);
      }
      return flattenPropertyGroupsByTaskStatus(groupFlatTasksByProperty(body.tasks));
    }

    if (Array.isArray(body.data)) {
      return normalizeAssignedTasksPayload(body.data);
    }
  }

  return { active: [], finished: [] };
}

export function mapAssignedTaskRow(task) {
  return {
    id: task.id,
    completed_sort: task.status === "completed" ? 1 : 0,
    priority_sort: getTaskPrioritySortRank(task.priority),
    property_address_display: formatPropertyFullAddress(task),
    description_display: truncateTableText(task.description?.trim() ? task.description : "") || TABLE_EMPTY_CELL,
    assigned_to_display: formatPersonDisplay(task.assigned_to),
    start_date_display: task.start_date ?? TABLE_EMPTY_CELL,
    link_raw: task.link?.trim() ? task.link : "",
    comment_display: truncateTableText(task.comment?.trim() ? task.comment : "") || TABLE_EMPTY_CELL,
    created_by_display: formatCreatedByDisplay(task.created_by, TABLE_EMPTY_CELL),
    property_id: task.property_id ?? task.property?.id ?? null,
    _task: task,
  };
}

/** Maps tasks to table rows (property order preserved by caller). */
export function mapAssignedTaskRowsGroupedByProperty(tasks) {
  return tasks.map(mapAssignedTaskRow);
}
